/**
 * Query/mutation hooks for Settings > Team, Profile, and Reset Context.
 * Grouped in one file since each tab only needs a handful of small hooks —
 * see src/queries/webhooks.js for the template this follows.
 */
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import localforage from "localforage";
import { request, ReqMethods } from "../lib/apiClient";

const { GET, POST, PUT, DELETE } = ReqMethods;

// ---- Team (Settings > Team tab) --------------------------------------------

export const locationUsersKeys = {
  all: ["locationUsers"],
  list: (locationId) => [...locationUsersKeys.all, "list", locationId],
};

// Was context/usersContext.js's getLocationUsers() — GET /user/chatusers via
// the useAppServices() registry (service.user.chatusers), token read from
// localforage on every call. Same query param + token shape, ported as-is.
//
// `enabled` lets callers defer the fetch until it's actually needed (e.g.
// UsersContext only requests it once a consumer — Team settings, a share
// modal, Nango, etc. — asks for it) instead of firing on every app load.
export function useLocationUsers(locationId, { enabled = true } = {}) {
  return useQuery({
    queryKey: locationUsersKeys.list(locationId),
    queryFn: async () => {
      const token = await localforage.getItem("token");
      const res = await request(GET, "/user/chatusers", {
        query: `locationId=${locationId}`,
        token,
      });
      return res?.data || [];
    },
    enabled: enabled && !!locationId,
  });
}

// Team modals call raw axios against /user/team-member directly (not through
// the useAppServices registry, which only has a differently-shaped add_team).
export function useAddTeamMember() {
  return useMutation({
    mutationFn: (payload) => request(POST, "/user/team-member", { payload }),
  });
}

export function useUpdateTeamMember() {
  return useMutation({
    mutationFn: ({ id, payload }) => request(PUT, `/user/team-member/${id}`, { payload }),
  });
}

export function useDeleteTeamMember() {
  return useMutation({
    mutationFn: (id) => request(DELETE, `/user/team-member/${id}`),
  });
}

// ---- Profile (Settings > Profile tab) --------------------------------------

// Shared by profile/index.js's name/email form and
// profile/components/EditFieldModal.jsx's single-field edit — same endpoint.
export function useUpdateChatUser() {
  return useMutation({
    mutationFn: (payload) => request(PUT, "/user/chatuser/update", { payload }),
  });
}

export function useUpdateChatUserPassword() {
  return useMutation({
    mutationFn: (payload) => request(PUT, "/user/chatuser/update/password", { payload }),
  });
}

// profile/components/EditFieldModal.jsx's business-name edit branch.
export function useUpdateAccount() {
  return useMutation({
    mutationFn: (payload) => request(PUT, "/accounts", { payload }),
  });
}

// ---- Reset Context (Settings > Reset Context tab) --------------------------

export const resetContextKeys = {
  all: ["resetContextSources"],
  list: (locationId) => [...resetContextKeys.all, "list", locationId],
};

export function useOnboardingSources(locationId) {
  return useQuery({
    queryKey: resetContextKeys.list(locationId),
    queryFn: async () => {
      const res = await request(GET, `/users/get-sources/${locationId}`);
      return res?.data ?? null;
    },
    enabled: !!locationId,
  });
}

// POST /users/onboarding is shared by ResetContext/index.jsx's own "Force
// Refresh" confirm and the three ChangeSourceModal children (LoadURLModal,
// TellUsMoreModal, UploadFileModal) — same endpoint, different payload shape
// per caller. On success, invalidates the sources list (replacing the old
// manual setRefetch(!refetch) + useEffect([refetch]) toggle) so History/
// SourceTab pick up the change.
export function useSubmitOnboarding(locationId) {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (payload) => request(POST, "/users/onboarding", { payload }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: resetContextKeys.list(locationId) });
    },
  });
}
